/**
 * Estimate Performance — dashboard KPI tile.
 *
 * Surfaces win-rate / quoted value / per-estimator leaderboard so admins can
 * see at a glance who's closing quotes and who needs coaching. Workers see
 * the same tile scoped to themselves only (no leaderboard).
 *
 * Data: GET /api/estimates/stats?days=30|90|365|0
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Trophy, Award, Loader2, FileSignature } from 'lucide-react';
import { EstimateFollowUps } from './EstimateFollowUps';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
  { key: 365, label: '1y' },
  { key: 0, label: 'All' },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function EstimatePerformanceTile() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/estimates/stats?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStats(res.data);
      } catch (e) {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, token]);

  if (loading) {
    return (
      <div className="ht-card p-5" data-testid="estimate-perf-tile">
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading estimate KPIs…</div>
      </div>
    );
  }

  const overall = stats?.overall || {};
  const leaderboard = stats?.leaderboard || [];
  const winRate = overall.win_rate || 0;
  const winRateColor = winRate >= 50 ? '#10B981' : winRate >= 25 ? '#F59E0B' : '#EF4444';

  return (
    <div className="ht-card p-5" data-testid="estimate-perf-tile">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
            <FileSignature className="w-5 h-5" style={{ color: '#10B981' }} />
          </div>
          <h3 className="text-white font-semibold">Estimate performance</h3>
        </div>
        <div className="flex gap-1" data-testid="perf-period-toggle">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setDays(p.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                days === p.key ? 'text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
              style={{ background: days === p.key ? '#1E88E5' : 'transparent', border: days === p.key ? 'none' : '1px solid #2D3748' }}
              data-testid={`perf-period-${p.key}`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {!stats?.scoped_to_self && <EstimateFollowUps token={token} days={7} />}

      {/* Headline KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="rounded-xl p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Win rate</p>
          <p className="text-2xl font-bold" style={{ color: winRateColor }} data-testid="perf-win-rate">{winRate}%</p>
          <p className="text-[10px] text-slate-500 mt-1">{overall.won || 0} won / {overall.lost || 0} lost{overall.pending ? ` · ${overall.pending} awaiting reply` : ''}</p>
        </div>
        <div className="rounded-xl p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Quoted</p>
          <p className="text-2xl font-bold text-white" data-testid="perf-quoted-value">{fmtMoney(overall.quoted_value)}</p>
          <p className="text-[10px] text-slate-500 mt-1">{overall.sent || 0} sent of {overall.created || 0} created</p>
        </div>
        <div className="rounded-xl p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Won value</p>
          <p className="text-2xl font-bold" style={{ color: '#10B981' }} data-testid="perf-won-value">{fmtMoney(overall.won_value)}</p>
          <p className="text-[10px] text-slate-500 mt-1">avg {fmtMoney(overall.avg_won_value)}</p>
        </div>
      </div>

      {/* Pipeline: every estimate in the period, including drafts */}
      {(overall.created || 0) > 0 && (
        <div className="mb-5" data-testid="perf-pipeline">
          <div className="flex h-2 rounded-full overflow-hidden" style={{ background: '#0F1923' }}>
            {[['drafts', '#64748B'], ['pending', '#1E88E5'], ['won', '#10B981'], ['lost', '#EF4444']].map(([k, c]) => (
              <div key={k} style={{ width: `${((overall[k] || 0) / overall.created) * 100}%`, background: c }} title={`${overall[k] || 0} ${k}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-slate-400">
            <span data-testid="perf-created"><span className="text-slate-200 font-semibold">{overall.created}</span> created · {fmtMoney(overall.pipeline_value)} total</span>
            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#64748B' }} />{overall.drafts || 0} draft{overall.drafts === 1 ? '' : 's'}</span>
            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#1E88E5' }} />{overall.pending || 0} awaiting</span>
            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#10B981' }} />{overall.won || 0} won</span>
            <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#EF4444' }} />{overall.lost || 0} lost</span>
          </div>
        </div>
      )}

      {/* Leaderboard (admin/supervisor only — workers see self) */}
      {!stats?.scoped_to_self && leaderboard.length > 0 && (
        <div data-testid="perf-leaderboard">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><Trophy className="w-3 h-3" /> Top estimators</p>
          <div className="space-y-1.5">
            {leaderboard.slice(0, 5).map((row, i) => (
              <div key={row.user_id} className="flex items-center justify-between text-sm" data-testid={`perf-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold" style={{
                    background: i === 0 ? 'rgba(245,158,11,0.2)' : i === 1 ? 'rgba(148,163,184,0.2)' : 'rgba(100,116,139,0.15)',
                    color: i === 0 ? '#F59E0B' : i === 1 ? '#94A3B8' : '#64748B',
                  }}>{i + 1}</span>
                  <span className="text-slate-200 truncate">{row.user_name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-400">{row.won}/{row.won + row.lost}</span>
                  <span className="font-semibold" style={{ color: row.win_rate >= 50 ? '#10B981' : row.win_rate >= 25 ? '#F59E0B' : '#94A3B8' }}>{row.win_rate}%</span>
                  <span className="text-emerald-400 font-semibold w-16 text-right">{fmtMoney(row.won_value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self-only summary for workers */}
      {stats?.scoped_to_self && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mt-2" data-testid="perf-self-summary">
          <Award className="w-4 h-4" style={{ color: '#10B981' }} />
          {overall.created > 0
            ? <>You quoted {overall.created} estimate{overall.created === 1 ? '' : 's'} this period — {overall.won} won, {overall.lost} lost.</>
            : <>No estimates yet this period. <button onClick={() => navigate('/estimates/new')} style={{ color: '#1E88E5' }}>Build your first one →</button></>}
        </div>
      )}

      {!stats?.scoped_to_self && leaderboard.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-3" data-testid="perf-empty">No estimates yet in this period{days ? ' — try a longer range' : ''}.</p>
      )}

      <button
        onClick={() => navigate('/estimates')}
        className="mt-4 w-full flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium text-slate-300 hover:text-white transition-colors"
        style={{ background: 'rgba(30,136,229,0.1)', color: '#1E88E5' }}
        data-testid="perf-view-all-btn"
      >
        <TrendingUp className="w-3 h-3" /> View all estimates
      </button>
    </div>
  );
}
